import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { hostname as apiHost } from "../../providers/api";

// E-commerce references loaded in several projects of the account, and which
// article the external API sends for each one.
// Backend: GET /apiIntegrationDiagnostics/:accountId/ecom-conflicts (admin only).
// Render it with key={accountId} so switching account starts from a clean state.

const PAGE_SIZE = 25;

const smallChip = (background) => ({
  background,
  color: "white",
  height: 18,
  fontSize: 11,
});

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "");

const candidateLabel = (candidate) =>
  `${candidate.projectName} · ${candidate.reference || candidate.articleName || candidate.articleId} · ${formatDate(candidate.createdAt)}`;

const EcomReferenceConflicts = ({ accountId, token }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (appliedSearch) params.set("search", appliedSearch);
      const response = await fetch(
        `${apiHost}/apiIntegrationDiagnostics/${accountId}/ecom-conflicts?${params.toString()}`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(`Request failed (${response.status}): ${body.message || response.statusText}`);
      }
      const data = await response.json();
      if (current === requestId.current) setResult(data);
    } catch (err) {
      if (current === requestId.current) setError(err.message);
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [accountId, token, page, appliedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const lastPage = result ? Math.max(1, Math.ceil(result.total / PAGE_SIZE)) : 1;

  return (
    <Box mt={3}>
      <Typography variant="subtitle1">E-commerce references in several projects</Typography>
      <Typography variant="body2" color="textSecondary">
        The API sends the most recently created article with calculated metrics; the others are not sent.
      </Typography>
      <Box display="flex" alignItems="center" mt={1} style={{ gap: 16 }}>
        <TextField
          id="ecom-conflicts-search"
          label="Search e-commerce reference"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") applySearch();
          }}
          size="small"
          variant="outlined"
        />
        <Button variant="outlined" color="primary" onClick={applySearch} disabled={loading}>
          Search
        </Button>
        {loading && <CircularProgress size={20} />}
      </Box>
      {error && <Typography color="error">{error}</Typography>}
      {result && result.data.length === 0 && !loading && (
        <Typography variant="body2">No e-commerce reference is loaded in more than one project.</Typography>
      )}
      {result && result.data.length > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ecom reference</TableCell>
                <TableCell>Sent by the API</TableCell>
                <TableCell>Not sent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.data.map((item) => (
                <TableRow key={item.ecomReference}>
                  <TableCell>{item.ecomReference}</TableCell>
                  <TableCell>
                    {item.served ? (
                      <>
                        <Chip size="small" label="Sent" style={smallChip("#2e7d32")} /> {candidateLabel(item.served)}
                      </>
                    ) : (
                      <>
                        <Chip size="small" label="None" style={smallChip("#d32f2f")} /> No article with metrics
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.candidates
                      .filter((candidate) => !candidate.served)
                      .map((candidate) => (
                        <div key={candidate.ecomReferenceId}>
                          {candidateLabel(candidate)}
                          {!candidate.hasMetrics && (
                            <Typography component="span" variant="caption" color="textSecondary">
                              {" "}(no metrics)
                            </Typography>
                          )}
                        </div>
                      ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" alignItems="center" mt={1} style={{ gap: 8 }}>
            <Button size="small" onClick={() => setPage(page - 1)} disabled={loading || page <= 1}>
              Previous
            </Button>
            <Typography variant="body2">
              Page {page} of {lastPage} · {result.total} references
            </Typography>
            <Button size="small" onClick={() => setPage(page + 1)} disabled={loading || page >= lastPage}>
              Next
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EcomReferenceConflicts;
